import AuthHttpService from "./AuthHttpService";
import { IBulkSubmitResponse, IUploadBatchResponse } from "services/types";

class ApiError extends Error {
  constructor(message: any, data: any) {
    super(message);

    this.name = "ApiError";

    if (typeof data === "object") {
      Object.keys(data).forEach(key => {
        // @ts-ignore
        this[key] = data[key];
      });
    }
  }
}

interface ICreateGalleryData {
  files: string[];
  publish: boolean;
  tags: string[];
  niches: string[];
  preferences: string;
}

interface IBulkSubmitRequest {
  files: string[];
}

export default class UploadService extends AuthHttpService {
  public constructor(readonly url?: string) {
    super(url);
  }

  async getUploadTicket({ md5, url }: {
    md5?: string | null;
    url?: string | null;
  }) {
    return await this.post("v2/upload", { md5, url, timeline: true });
  }

  async getUploadTicketBatch(count: number): Promise<IUploadBatchResponse> {
    return await this.post("v2/upload/batch", { count });
  }

  async getTicketStatus({ id }: { id: string }) {
    return await this.get(`v2/upload/${id}/status`);
  }

  async checkUploadStatus(gifId: string) {
    return await this.get(`v1/gifs/fetch/status/${gifId}`);
  }

  async createGallery(data: ICreateGalleryData) {
    return await this.post("v2/gallery", data);
  }

  async checkUploadGalleryStatus(galleryId: string) {
    return await this.get(`v2/gallery/${galleryId}/status`);
  }

  putFile({ url, file, callback }: { url: string; file: File; callback: Function | undefined }) {
    // eslint-disable-next-line
    console.log(`Putting a file to ${url}`);

    const xhr = new XMLHttpRequest();

    xhr.open("PUT", url);
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader("Content-Type", file.type);
    xhr.responseType = "json";

    xhr.upload.onprogress = progressEvent => {
      if (progressEvent.lengthComputable && callback) {
        callback(progressEvent);
      }
    };

    return new Promise((resolve, reject) => {
      xhr.onload = progressEvent => {
        // @ts-ignore
        const { status, statusText } = progressEvent.target;

        // @ts-ignore
        const { response } = progressEvent.target;
        // eslint-disable-next-line
        if (status >= 300) {
          return reject(
            new ApiError(statusText, {
              status,
              response
            })
          );
        }

        if (response) {
          // eslint-disable-next-line
          console.log("putFile response:", response);
        }

        return resolve(response);
      };

      xhr.onerror = progressEvent => {
        // @ts-ignore
        const { status, statusText, response } = progressEvent.target;

        return reject(
          new ApiError(`Network error: ${statusText}`, {
            status,
            response
          })
        );
      };

      xhr.send(file);
    });
  }

  async submitGifs(params: IBulkSubmitRequest): Promise<IBulkSubmitResponse> {
    return await this.post("v2/gifs/submit/bulk", {
      files: params.files,
    });
  }

  async submitGif({
    ticketID,
    tags,
    niches,
    isPrivate,
    sexuality,
    keepAudio,
    description,
    startTime,
    endTime
  }: {
    ticketID: string | null;
    isPrivate: boolean;
    keepAudio: boolean;
    tags: string[];
    niches: string[];
    sexuality: string;
    startTime: number;
    description: string | null;
    endTime: number;
  }) {
    return await this.post("v2/gifs/submit", {
      ticket: ticketID,
      tags,
      private: isPrivate,
      keepAudio,
      description,
      niches,
      sexuality,
      cut: {
        start: startTime,
        duration: endTime - startTime
      }
    });
  }
}
